import React, { useState, useEffect, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { compose } from 'recompose';
import styled from 'styled-components';
import withUpdateStallStatus from '../../../mutations/UpdateStallStatus';

import { restAPI } from 'Lib/api';
import { HeadingOne } from 'Components/Headings';
import Table from '../../../components/Table.DEPRECATED';
import ContextSnackbar from '../../../components/Snackbar';
import ExportButton from '../../../components/Button/ExportButton';
import { STALLS_FOR_OPS_TABLE } from 'Queries/Ops/StallsForOpsTable';
import withTableActions from '../../../enhancers/withTableActions';

import colors from '../../../styles/Colors';

import { withSnackbarContextActions } from 'Store/SnackbarContext';
import { UserContext } from 'Store/UserContext';
import { isMobile } from 'react-device-detect';

export const OperationsTableBase = props => {
  const {
    user: { venues = [], id }
  } = useContext(UserContext);

  const [firstVenue = {}] = venues;
  const { id: venueId } = firstVenue;

  const {
    onEditClick,
    getVariables,
    updateFilterTextField,
    updateDates,
    onSubmitFilter,
    onClearFilters,
    onRequestSort,
    onClearSingleFilter,
    updateCheckedRows,
    onMasterCheckboxClick,
    handleTableChangePage,
    handleTableChangeRowsPerPage,
    showSnackbar
  } = props;
  const [sortName, setSortName] = useState('startDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(['name', 'ASC']);
  const [filterBy, setFilterBy] = useState(null);
  const [buildingId, setBuildingId] = useState('');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [checkedRows, setCheckedRows] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);

  const availableFilters = ['buildingId', 'name', 'status', 'startDate', 'endDate'];

  useEffect(() => {
    if (loadingReport) {
      showSnackbar('Generating report. Download will begin shortly', {
        duration: 5000
      });
    }
  }, [loadingReport]);

  const variables =
    getVariables &&
    getVariables({
      orderBy,
      filterBy,
      rowsPerPage,
      page
    });

  const { data, loading, error } = useQuery(STALLS_FOR_OPS_TABLE, {
    variables: { ...variables },
    pollInterval: 5000,
    fetchPolicy: 'network-only'
  });

  const rows = [
    {
      id: 'checkable',
      numeric: false,
      disablePadding: false,
      label: ''
    },
    {
      id: 'building',
      numeric: false,
      disablePadding: false,
      label: 'STALL LOCATION'
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'STALL #',
      sortable: true
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'STATUS',
      sortable: true
    },
    {
      id: 'departureDate',
      numeric: false,
      disablePadding: false,
      label: 'CHECK OUT DATE',
      sortable: false
    },
    {
      id: 'nextReservation',
      numeric: false,
      disablePadding: false,
      label: 'NEXT CHECK IN',
      sortable: false
    }
  ];

  const statusOptions = [
    { value: 'clean', label: 'CLEAN' },
    { value: 'dirty', label: 'DIRTY' },
    { value: 'occupied', label: 'OCCUPIED' }
  ];

  const getBuildingName = buildings => {
    if (!buildingId) return '';
    const match = buildings.find(building => {
      return building.id === buildingId;
    });
    if (match) return match.name;
  };

  const getStallBuildingNameOptions = buildings => {
    return buildings.map(building => {
      return { value: building.id, label: building.name };
    });
  };

  const onBulkOptionClick = status => {
    const ids = checkedRows.map(row => row.id);
    ids.map(id => {
      return props.updateStallStatus({ id, status }, variables);
    });
    setCheckedRows([]);
  };

  const updateStallStatus = stall => event => {
    const { id } = stall;
    const status = event.target.value;
    return props.updateStallStatus({ id, status }, variables);
  };

  const reservationStalls = data?.venue?.stalls || [];

  const operationsTableData = () => {
    return reservationStalls.map(stall => {
      const stallData = {
        id: stall.id,
        status: {
          id: 'STATUS',
          value: stall.status,
          label: 'STATUS',
          cb: updateStallStatus(stall),
          options: statusOptions
        },
        departureDate: stall.currentOrder ? stall.currentOrder.lastDepartureDate : '-',
        nextReservation: stall.nextOrder ? stall.nextOrder.nextReservationDate : '-',
        building: stall.building.name,
        name: stall.name
      };
      return {
        ...stallData
      };
    });
  };

  const updateFilterStatus = event => {
    if (event.target) {
      const { value } = event.target;
      const match = statusOptions.find(option => option.value === value);
      if (match) return setStatus(match.value);
    }
    return setStatus('');
  };

  const updateStallLocationFilter = buildings => event => {
    if (event.target) {
      const { value } = event.target;
      const match = buildings.find(building => {
        return building.id === value;
      });
      if (match) return setBuildingId(match.id);
    }
    return setBuildingId('');
  };

  const filters = [
    {
      key: 'buildingId',
      label: 'STALL LOCATION',
      type: 'select',
      value: getBuildingName(data ? data.venue.buildings : []),
      selectedOption: buildingId,
      options: getStallBuildingNameOptions(data ? data.venue.buildings : []),
      cb: updateStallLocationFilter(data ? data.venue.buildings : [])
    },
    {
      key: 'name',
      label: 'STALL NUMBER',
      type: 'text',
      value: name,
      cb: updateFilterTextField && updateFilterTextField(setName)
    },
    {
      key: 'status',
      label: 'STATUS',
      type: 'select',
      value: status ? status.toUpperCase() : null,
      selectedOption: status,
      options: statusOptions,
      cb: updateFilterStatus
    },
    {
      key: 'endDate',
      label: 'CHECK OUT DATE',
      type: 'date',
      plural: false,
      value: endDate,
      showPast: false,
      cb: updateDates && updateDates('single-end', setStartDate, setEndDate)
    },
    {
      key: 'startDate',
      label: 'NEXT CHECK IN',
      type: 'date',
      plural: false,
      value: startDate,
      showPast: false,
      cb: updateDates && updateDates('single', setStartDate, setEndDate)
    }
  ];

  if (loading) return 'Loading...';
  if (error) {
    // eslint-disable-next-line
    console.error(error);
    return 'uh oh...';
  }

  const handleExportStalls = async () => {
    if (!venueId) return;

    setLoadingReport(true);
    let path = 'ops/stalls-report';
    let fileName = 'StallsReport.xlsx';

    const setFilters = { buildingId, name, status, startDate, endDate };
    const exportFilters = {};
    Object.keys(setFilters).forEach(filterKey => {
      if (setFilters[filterKey]) {
        exportFilters[filterKey] = setFilters[filterKey];
      }
    });

    try {
      await restAPI({
        path,
        method: 'POST',
        body: {
          venueId,
          userId: id,
          options: {
            filterBy: exportFilters,
            orderBy: ['stall', 'ASC']
          }
        },
        header: {
          responseType: 'blob'
        },
        isDownload: true,
        fileName
      });
    } catch (err) {
      showSnackbar('Unable to download report', {
        duration: 5000,
        error: true
      });
    }

    setLoadingReport(false);
  };

  return (
    <OperationsTable isMobile={isMobile}>
      <ContextSnackbar />
      <div className="page-header">
        <div className="header-wrapper">
          <HeadingOne label="STALLS" className="" />
          <div>
            <ExportButtonBase isMobile={isMobile} className="" label={isMobile ? '' : 'EXPORT REPORT'} onClick={handleExportStalls} loading={loadingReport} />
          </div>
        </div>
      </div>

      <Table
        filters={filters}
        rows={rows}
        data={operationsTableData()}
        onSubmit={onSubmitFilter(availableFilters, { buildingId, name: name, status, startDate, endDate }, setFilterBy, setPage)}
        onClearFilters={onClearFilters(
          availableFilters,
          {
            buildingId: [buildingId, setBuildingId],
            name: [name, setName],
            status: [status, setStatus],
            startDate: [startDate, setStartDate],
            endDate: [endDate, setEndDate]
          },
          setFilterBy
        )}
        onEditClick={onEditClick}
        orderBy={sortName}
        order={sortOrder}
        onRequestSort={onRequestSort({ sortName, sortOrder }, { setSortName, setSortOrder, setOrderBy })}
        onCheckboxClick={updateCheckedRows(checkedRows, setCheckedRows)}
        selectedRows={{
          rows: checkedRows,
          cb: onBulkOptionClick,
          options: statusOptions
        }}
        onMasterCheckboxClick={onMasterCheckboxClick(reservationStalls, checkedRows, setCheckedRows)}
        view="operations"
        onClearSingleFilter={onClearSingleFilter(filterBy, setFilterBy)}
        allTableRows={data.allVenueStalls.stalls}
        count={data.allVenueStalls?.stalls?.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleTableChangePage(setPage)}
        onChangeRowsPerPage={handleTableChangeRowsPerPage(setRowsPerPage, setPage)}
      />
    </OperationsTable>
  );
};

const OperationsTable = styled.div`
  .page-header {
    text-align: left;
    border-bottom: 1px solid rgb(200, 214, 229);
    &&& {
      margin: 0;
    }
  }

  .header-wrapper {
    margin: 20px 0px;

    ${({ isMobile }) =>
      isMobile &&
      `
      margin: 20px 20px;
    `}

    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const ExportButtonBase = styled(ExportButton)`
  &&& {
    background-color: ${colors.secondary.active};
    padding-top: 10px;

    ${({ isMobile }) =>
      isMobile &&
      `
      width: 40px;
    `}

    svg {
      position: relative;
      top: -2px;
    }
  }
`;

export default compose(withUpdateStallStatus, withTableActions, withSnackbarContextActions)(OperationsTableBase);
